import { DeviceToolError } from "../errors";
import { readBlobArrayBuffer } from "../files/ChunkedFileReader";
import { isRemoteFirmwareFile } from "../files/RemoteFirmwareFile";

export interface AmlogicImageItem {
  index: number;
  extension: string;
  name: string;
  offset: number;
  size: number;
  end: number;
  fileType?: number;
}

export interface AmlogicImageReport {
  format: "Amlogic USB burn package";
  recordCount: number;
  fileSize: number;
  items: AmlogicImageItem[];
  boundsValid: boolean;
  overlaps: boolean;
  checksum: "not-present";
  flashSupported: boolean;
}

const IMAGE_MAGIC = 0x27b51956;
const HEADER_SIZE = 0x40;
const LEGACY_RECORD_SIZE = 0x240;
const ITEM_V1_SIZE = 0x80;
const ITEM_V2_SIZE = 0x240;
const ITEM_NAME_LEN_V1 = 0x20;
const ITEM_NAME_LEN_V2 = 0x100;
const MAX_ITEM_COUNT = 48;

interface ImageLayout {
  littleEndian: boolean;
  wideFields: boolean;
  version: 1 | 2;
  recordSize: number;
  nameLength: number;
  count: number;
}

function cString(bytes: Uint8Array): string {
  const end = bytes.indexOf(0);
  const text = new TextDecoder("utf-8", { fatal: false }).decode(end === -1 ? bytes : bytes.slice(0, end));
  return [...text].filter((character) => character.charCodeAt(0) >= 0x20 && character.charCodeAt(0) <= 0x7e).join("").trim();
}

function looksLikeField(value: string): boolean {
  return value.length > 0 && value.length <= 256 && [...value].every((character) => {
    const code = character.charCodeAt(0);
    return code >= 0x20 && code <= 0x7e;
  });
}

function safeNumber(value: bigint, label: string): number {
  if (value < 0n || value > BigInt(Number.MAX_SAFE_INTEGER)) throw new DeviceToolError("INVALID_IMAGE", `${label} vượt giới hạn an toàn của trình duyệt.`);
  return Number(value);
}

function detectLayout(header: Uint8Array, fileSize: number): ImageLayout {
  const view = new DataView(header.buffer, header.byteOffset, header.byteLength);
  const littleMagic = view.getUint32(0x08, true);
  const bigMagic = view.getUint32(0x08, false);
  const hasMagic = littleMagic === IMAGE_MAGIC || bigMagic === IMAGE_MAGIC;
  const littleEndian = littleMagic === IMAGE_MAGIC;
  const versionValue = view.getUint32(0x04, littleEndian);
  const version = versionValue === 1 || versionValue === 2 ? versionValue : undefined;
  const countFromHeader = view.getUint32(0x18, littleEndian);
  const legacyCount = header[0x18];

  if (hasMagic && version && countFromHeader > 0 && countFromHeader <= MAX_ITEM_COUNT) {
    const recordSize = version === 1 ? ITEM_V1_SIZE : ITEM_V2_SIZE;
    const tableSize = HEADER_SIZE + countFromHeader * recordSize;
    if (tableSize <= fileSize) return { littleEndian, wideFields: true, version, recordSize, nameLength: version === 1 ? ITEM_NAME_LEN_V1 : ITEM_NAME_LEN_V2, count: countFromHeader };
  }

  if (legacyCount > 0 && legacyCount <= 255 && HEADER_SIZE + legacyCount * LEGACY_RECORD_SIZE <= fileSize) {
    return { littleEndian: false, wideFields: false, version: 2, recordSize: LEGACY_RECORD_SIZE, nameLength: ITEM_NAME_LEN_V2, count: legacyCount };
  }
  throw new DeviceToolError("INVALID_IMAGE", "Không nhận diện được magic/version/header của Amlogic package.");
}

function readUint64(view: DataView, offset: number, littleEndian: boolean, label: string): number {
  return safeNumber(view.getBigUint64(offset, littleEndian), label);
}

/**
 * Parses both the legacy record table and the public Optimus/Amlogic V1/V2
 * firmware header. Only the header/table are read; payload data is streamed
 * later by the burn adapter.
 */
export async function parseAmlogicImage(file: Blob): Promise<AmlogicImageReport> {
  const headerBytes = new Uint8Array(await readBlobArrayBuffer(file.slice(0, HEADER_SIZE)));
  if (headerBytes.byteLength < HEADER_SIZE) throw new DeviceToolError("INVALID_IMAGE", "Thiếu header Amlogic.");
  const layout = detectLayout(headerBytes, file.size);
  const tableSize = HEADER_SIZE + layout.count * layout.recordSize;
  const table = new Uint8Array(await readBlobArrayBuffer(file.slice(0, tableSize)));
  const view = new DataView(table.buffer, table.byteOffset, table.byteLength);
  const items: AmlogicImageItem[] = [];

  for (let index = 0; index < layout.count; index += 1) {
    const base = HEADER_SIZE + index * layout.recordSize;
    const nameBase = base + 0x20;
    const subtypeBase = layout.version === 1 ? base + 0x40 : base + 0x120;
    const fieldEnd = base + layout.recordSize;
    const extension = cString(table.slice(nameBase, Math.min(nameBase + layout.nameLength, fieldEnd)));
    const name = cString(table.slice(subtypeBase, Math.min(subtypeBase + layout.nameLength, fieldEnd)));
    const offset = layout.wideFields ? readUint64(view, base + 0x10, layout.littleEndian, `Item ${index + 1} offset`) : view.getUint32(base + 0x10, false);
    const size = layout.wideFields ? readUint64(view, base + 0x18, layout.littleEndian, `Item ${index + 1} size`) : view.getUint32(base + 0x18, false);
    const fileType = view.getUint32(base + 0x04, layout.littleEndian);
    const end = offset + size;
    if (!looksLikeField(extension) || !looksLikeField(name) || size === 0 || offset < tableSize || end < offset || end > file.size) {
      throw new DeviceToolError("INVALID_IMAGE", `Item ${index + 1} có offset/size hoặc tên không hợp lệ (name="${name}", type="${extension}", offset=0x${offset.toString(16)}, size=0x${size.toString(16)}, endian=${layout.littleEndian ? "LE" : "BE"}, version=${layout.version}).`);
    }
    items.push({ index, extension, name, offset, size, end, fileType });
  }

  const sorted = [...items].sort((a, b) => a.offset - b.offset || a.end - b.end);
  const overlaps = sorted.some((item, index) => {
    if (index === 0) return false;
    const previous = sorted[index - 1];
    const sharesPayload = item.offset === previous.offset && item.end === previous.end;
    return item.offset < previous.end && !sharesPayload;
  });
  if (overlaps) throw new DeviceToolError("INVALID_IMAGE", "Các item trong package bị chồng lấn.");
  const itemKeys = new Set<string>();
  for (const item of items) {
    const key = `${item.extension.toLowerCase()}\u0000${item.name.toLowerCase()}`;
    if (itemKeys.has(key)) throw new DeviceToolError("INVALID_IMAGE", "Package chứa cặp type/name bị lặp.");
    itemKeys.add(key);
  }
  return { format: "Amlogic USB burn package", recordCount: layout.count, fileSize: file.size, items, boundsValid: true, overlaps: false, checksum: "not-present", flashSupported: true };
}

export async function inspectAmlogicImage(file: Blob): Promise<AmlogicImageReport> {
  if (isRemoteFirmwareFile(file)) return parseAmlogicImage(file);
  const worker = new Worker(new URL("../../workers/amlogic-image.worker.ts", import.meta.url), { type: "module" });
  return new Promise((resolve, reject) => {
    worker.onmessage = (event: MessageEvent<{ type: string; result?: AmlogicImageReport; error?: string }>) => {
      worker.terminate();
      if (event.data.type === "done" && event.data.result) resolve(event.data.result);
      else reject(new DeviceToolError("INVALID_IMAGE", event.data.error));
    };
    worker.onerror = (event) => {
      worker.terminate();
      reject(new DeviceToolError("INVALID_IMAGE", event.message));
    };
    worker.postMessage({ file });
  });
}
